import React, { useCallback, useEffect, useState } from 'react';
import './SocialContent.css';
import { openSocialCampaignInCreativeStudio } from './socialCampaignBridge';

const PAGE_SIZE = 12;
const PRESENTATION_TYPE_LABELS = {
  single_image: 'Single images',
  image_gallery: 'Image galleries',
  single_video: 'Videos',
  mixed_gallery: 'Mixed galleries',
  text_card: 'Text posts',
};

export function formatSocialPostDate(value) {
  if (!value) return 'Unknown publication time';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown publication time' : date.toLocaleString();
}

export function resolveSocialPlaybackMode(selectedDevice) {
  return selectedDevice?.interfaceMode === 'external_app' || selectedDevice?.externalApp
    ? 'system'
    : 'suitestream';
}

export function getSocialStateLabel(state) {
  return ({
    discovered: 'Needs review',
    ready: 'Ready',
    ignored: 'Ignored',
    unsupported: 'Unavailable',
    failed: 'Needs attention',
    processing: 'Importing',
    published: 'Published',
  })[state] || String(state || 'Unknown').replaceAll('_', ' ');
}

export function getSocialMediaLabel(media = []) {
  const items = Array.isArray(media) ? media : [];
  const imageCount = items.filter((item) => item.media_type === 'image').length;
  const videoCount = items.filter((item) => item.media_type === 'video').length;
  if (!items.length) return 'Text post';
  if (imageCount === 1 && !videoCount) return 'Single image';
  if (imageCount > 1 && !videoCount) return `${imageCount} image gallery`;
  if (videoCount === 1 && !imageCount) return 'Video';
  return `${items.length} media items`;
}

function SocialMediaPreview({ post }) {
  const media = Array.isArray(post.media) ? post.media : [];
  const [activeIndex, setActiveIndex] = useState(0);
  const activeMedia = media[activeIndex] || null;
  const naturalRatio = activeMedia?.source_width && activeMedia?.source_height
    ? `${activeMedia.source_width} / ${activeMedia.source_height}`
    : undefined;

  useEffect(() => { setActiveIndex(0); }, [post.id]);

  return (
    <div className="social-post__media-shell">
      <div className="social-post__preview" style={naturalRatio ? { aspectRatio: naturalRatio } : undefined}>
        {activeMedia?.media_type === 'image' && activeMedia.source_url ? (
          <img src={activeMedia.source_url} alt={`Facebook post media ${activeIndex + 1} of ${media.length}`} referrerPolicy="no-referrer" />
        ) : activeMedia?.media_type === 'video' && activeMedia.source_url ? (
          <video src={activeMedia.source_url} controls playsInline preload="metadata" aria-label={`Facebook post video ${activeIndex + 1} of ${media.length}`} />
        ) : (
          <div className="social-post__placeholder">{post.presentation_type === 'text_card' ? 'Text-only Facebook post' : 'No media preview available'}</div>
        )}
        {media.length > 1 && <span className="social-post__media-count">{activeIndex + 1} / {media.length}</span>}
      </div>
      {media.length > 1 && (
        <div className="social-post__thumbnails" aria-label="Post media">
          {media.map((item, index) => (
            <button
              key={item.id || `${post.id}-${index}`}
              type="button"
              className={index === activeIndex ? 'active' : ''}
              onClick={() => setActiveIndex(index)}
              aria-label={`Show media ${index + 1}`}
              aria-pressed={index === activeIndex}
            >
              {item.media_type === 'image' && item.source_url
                ? <img src={item.source_url} alt="" referrerPolicy="no-referrer" loading="lazy" />
                : <span aria-hidden="true">▶</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AutomationAccount({ account, templateGroups, busy, onSave }) {
  const [rules, setRules] = useState(account.rules || []);

  useEffect(() => { setRules(account.rules || []); }, [account]);

  const updateRule = (presentationType, updater) => {
    setRules((current) => current.map((rule) => rule.presentationType === presentationType ? updater(rule) : rule));
  };

  const toggleTemplate = (presentationType, templateKey) => {
    updateRule(presentationType, (rule) => {
      const selected = rule.templateKeys.includes(templateKey);
      if (selected && rule.templateKeys.length === 1) return rule;
      return {
        ...rule,
        templateKeys: selected
          ? rule.templateKeys.filter((key) => key !== templateKey)
          : [...rule.templateKeys, templateKey],
      };
    });
  };

  return (
    <div className="social-automation__account">
      <div className="social-automation__account-heading">
        <div><strong>{account.account_name || 'Facebook Page'}</strong><span>New posts will use these rules once automatic processing is enabled.</span></div>
        <span className="social-post__state social-post__state--ready">Connected</span>
      </div>
      <div className="social-automation__rules">
        {rules.map((rule) => {
          const templates = templateGroups[rule.presentationType] || [];
          return (
            <div className="social-automation__rule" key={rule.presentationType}>
              <label className="social-automation__switch">
                <input
                  type="checkbox"
                  checked={rule.autoPrepareEnabled}
                  onChange={(event) => updateRule(rule.presentationType, (current) => ({ ...current, autoPrepareEnabled: event.target.checked }))}
                  disabled={busy}
                />
                <span><strong>{PRESENTATION_TYPE_LABELS[rule.presentationType] || rule.presentationType}</strong><small>Automatically import and prepare</small></span>
              </label>
              <div className="social-automation__templates">
                {templates.map((template) => (
                  <label key={template.templateKey} className={rule.templateKeys.includes(template.templateKey) ? 'selected' : ''}>
                    <input
                      type="checkbox"
                      checked={rule.templateKeys.includes(template.templateKey)}
                      onChange={() => toggleTemplate(rule.presentationType, template.templateKey)}
                      disabled={busy}
                    />
                    <span>{template.label}</span>
                  </label>
                ))}
              </div>
              <small className="social-automation__rotation">Selected templates rotate in order, then repeat.</small>
            </div>
          );
        })}
      </div>
      <button className="social-automation__save" type="button" onClick={() => onSave(account.id, rules)} disabled={busy}>
        {busy ? 'Saving…' : 'Save automation rules'}
      </button>
    </div>
  );
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

export default function SocialContent({ site, selectedDevice }) {
  const [result, setResult] = useState({ posts: [], page: 1, pageSize: PAGE_SIZE, total: 0 });
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyPostId, setBusyPostId] = useState(null);
  const [message, setMessage] = useState('');
  const [templateGroups, setTemplateGroups] = useState({});
  const [automationAccounts, setAutomationAccounts] = useState([]);
  const [automationOpen, setAutomationOpen] = useState(false);
  const [busyAutomationAccountId, setBusyAutomationAccountId] = useState(null);

  const loadPosts = useCallback(async () => {
    if (!site?.id) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ siteId: String(site.id), provider: 'facebook', page: String(page), pageSize: String(PAGE_SIZE) });
      if (stateFilter) params.set('state', stateFilter);
      const response = await fetch(`/api/social/posts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        cache: 'no-store',
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to load social content');
      setResult(body);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'Unable to load social content'));
    } finally {
      setLoading(false);
    }
  }, [page, site?.id, stateFilter]);

  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => { setPage(1); }, [site?.id, stateFilter]);

  useEffect(() => {
    if (!site?.id) return undefined;
    let active = true;
    const loadTemplates = async () => {
      try {
        const requestOptions = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, cache: 'no-store' };
        const [templateResponse, automationResponse] = await Promise.all([
          fetch(`/api/social/presentation-templates?siteId=${encodeURIComponent(site.id)}`, requestOptions),
          fetch(`/api/social/automation?siteId=${encodeURIComponent(site.id)}`, requestOptions),
        ]);
        const [body, automationBody] = await Promise.all([templateResponse.json(), automationResponse.json()]);
        if (!templateResponse.ok) throw new Error(body.error || 'Unable to load presentation templates');
        if (!automationResponse.ok) throw new Error(automationBody.error || 'Unable to load automation settings');
        if (active) {
          setTemplateGroups(Object.fromEntries((body.groups || []).map((group) => [group.presentationType, group.templates || []])));
          setAutomationAccounts(automationBody.accounts || []);
        }
      } catch (templateError) {
        if (active) setError(getErrorMessage(templateError, 'Unable to load presentation templates'));
      }
    };
    loadTemplates();
    return () => { active = false; };
  }, [site?.id]);

  const saveAutomation = async (accountId, rules) => {
    setBusyAutomationAccountId(accountId);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/social/accounts/${accountId}/automation`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: site.id, rules }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to save automation settings');
      setAutomationAccounts((accounts) => accounts.map((account) => Number(account.id) === Number(accountId)
        ? { ...account, rules: body.rules }
        : account));
      setMessage('New-post automation preferences saved. Automatic processing will be connected in a later step.');
    } catch (automationError) {
      setError(getErrorMessage(automationError, 'Unable to save automation settings'));
    } finally { setBusyAutomationAccountId(null); }
  };

  const updateTemplate = async (post, templateKey) => {
    setBusyPostId(post.id);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/social/posts/${post.id}/template`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: site.id, presentationType: post.presentation_type, templateKey }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to update presentation template');
      setResult((current) => ({
        ...current,
        posts: (current.posts || []).map((item) => item.id === post.id
          ? { ...item, template_key: body.templateKey, template_version: body.templateVersion }
          : item),
      }));
      setMessage(post.overlay_id
        ? 'Template selected. Preview the presentation to refresh it.'
        : 'Presentation template selected.');
    } catch (templateError) {
      setError(getErrorMessage(templateError, 'Unable to update presentation template'));
    } finally { setBusyPostId(null); }
  };

  const ignorePost = async (postId) => {
    setBusyPostId(postId);
    setError('');
    try {
      const response = await fetch(`/api/social/posts/${postId}/ignored`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: site.id }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to ignore this post');
      await loadPosts();
    } catch (updateError) {
      setError(getErrorMessage(updateError, 'Unable to ignore this post'));
    } finally {
      setBusyPostId(null);
    }
  };

  const restorePost = async (postId) => {
    setBusyPostId(postId);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/social/posts/${postId}/restore`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: site.id }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to restore this post');
      setMessage('Facebook post restored to the review inbox.');
      await loadPosts();
    } catch (restoreError) {
      setError(getErrorMessage(restoreError, 'Unable to restore this post'));
    } finally { setBusyPostId(null); }
  };

  const importPost = async (postId) => {
    setBusyPostId(postId);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/social/posts/${postId}/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: site.id }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to import this post');
      setMessage(body.alreadyImported ? 'This Facebook media was already imported.' : `${body.creativeIds?.length || 1} Facebook image${body.creativeIds?.length === 1 ? '' : 's'} imported into SoundScreen media.`);
      await loadPosts();
    } catch (importError) {
      setError(getErrorMessage(importError, 'Unable to import this post'));
    } finally { setBusyPostId(null); }
  };

  const openTrackedCta = async (postId) => {
    const popup = window.open('', '_blank');
    if (popup) popup.opener = null;
    setBusyPostId(postId);
    setError('');
    try {
      const response = await fetch(`/api/social/posts/${postId}/cta`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: site.id }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to create tracked CTA');
      if (popup) popup.location = body.url;
      else window.open(body.url, '_blank', 'noopener,noreferrer');
    } catch (ctaError) {
      if (popup) popup.close();
      setError(getErrorMessage(ctaError, 'Unable to create tracked CTA'));
    } finally { setBusyPostId(null); }
  };

  const createPresentation = async (postId, { openPreview = true } = {}) => {
    const popup = openPreview ? window.open('', '_blank') : null;
    if (popup) popup.opener = null;
    setBusyPostId(postId);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/social/posts/${postId}/presentation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: site.id }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to create social presentation');
      setMessage(body.alreadyCreated ? 'Social presentation refreshed.' : 'Social presentation created and added to SoundScreen overlays.');
      if (popup) popup.location = body.previewUrl;
      else if (openPreview) window.open(body.previewUrl, '_blank', 'noopener,noreferrer');
      return body;
    } catch (presentationError) {
      if (popup) popup.close();
      setError(getErrorMessage(presentationError, 'Unable to create social presentation'));
      return null;
    } finally { setBusyPostId(null); }
  };

  const createCampaign = async (post) => {
    setBusyPostId(post.id);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`/api/social/posts/${post.id}/campaign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId: site.id }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to create campaign');
      openSocialCampaignInCreativeStudio({ campaign: body.campaign, siteId: site.id });
    } catch (campaignError) {
      setError(getErrorMessage(campaignError, 'Unable to create campaign'));
      setBusyPostId(null);
    }
  };

  const playPresentation = async (postId) => {
    if (!selectedDevice?.id) {
      setError('Select a TV before playing this social ad.');
      return;
    }
    setBusyPostId(postId);
    setError('');
    setMessage('');
    try {
      const presentation = await createPresentation(postId, { openPreview: false });
      if (!presentation?.overlayId) return;
      setBusyPostId(postId);
      const response = await fetch('/api/overlays/play', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId: selectedDevice.id,
          overlayId: presentation.overlayId,
          mode: resolveSocialPlaybackMode(selectedDevice),
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Unable to play this social ad');
      setMessage(`Social ad sent to ${selectedDevice.name || 'the selected TV'}.`);
    } catch (playError) {
      setError(getErrorMessage(playError, 'Unable to play this social ad'));
    } finally { setBusyPostId(null); }
  };

  const pageCount = Math.max(1, Math.ceil(Number(result.total || 0) / PAGE_SIZE));

  return (
    <section className="social-content" aria-labelledby="social-content-title">
      <div className="social-content__header">
        <div>
          <p className="social-content__eyebrow">Facebook</p>
          <h2 id="social-content-title">Social Content</h2>
          <p>Review posts synchronized from the Facebook Pages connected to {site?.name || 'this site'}.</p>
        </div>
        <button type="button" onClick={loadPosts} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      <div className="social-content__filters" role="group" aria-label="Filter social posts">
        {[["", 'All'], ['discovered', 'Needs review'], ['ready', 'Ready'], ['ignored', 'Ignored']].map(([value, label]) => (
          <button key={label} type="button" className={stateFilter === value ? 'active' : ''} onClick={() => setStateFilter(value)}>{label}</button>
        ))}
      </div>

      {!!automationAccounts.length && (
        <section className="social-automation">
          <button className="social-automation__toggle" type="button" onClick={() => setAutomationOpen((value) => !value)} aria-expanded={automationOpen}>
            <span><strong>New post automation</strong><small>Choose how each kind of future Facebook post should be presented.</small></span>
            <span aria-hidden="true">{automationOpen ? '−' : '+'}</span>
          </button>
          {automationOpen && (
            <div className="social-automation__body">
              <p>These preferences are ready for the future automatic-import worker. They do not schedule or play content on screens.</p>
              {automationAccounts.map((account) => (
                <AutomationAccount
                  key={account.id}
                  account={account}
                  templateGroups={templateGroups}
                  busy={busyAutomationAccountId === account.id}
                  onSave={saveAutomation}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {error && <div className="social-content__error" role="alert">{error}</div>}
      {message && <div className="social-content__message" role="status">{message}</div>}
      {!loading && !result.posts?.length && <div className="social-content__empty">No synchronized Facebook posts match this filter.</div>}

      <div className="social-content__grid">
        {(result.posts || []).map((post) => {
          const importablePost = post.media?.length > 0 && post.media.every((item) => ['image', 'video'].includes(item.media_type) && item.source_url);
          const presentationReady = post.state === 'ready' || post.presentation_type === 'text_card';
          const availableTemplates = templateGroups[post.presentation_type] || [];
          return (
            <article className="social-post" key={post.id}>
              <SocialMediaPreview post={post} />
              <div className="social-post__body">
                <div className="social-post__meta"><span>{post.account_name}</span><span className={`social-post__state social-post__state--${post.state}`}>{getSocialStateLabel(post.state)}</span></div>
                <div className="social-post__type">{getSocialMediaLabel(post.media)}</div>
                {!!availableTemplates.length && (
                  <label className="social-post__template">
                    <span>Presentation</span>
                    <select
                      value={post.template_key || availableTemplates[0].templateKey}
                      onChange={(event) => updateTemplate(post, event.target.value)}
                      disabled={busyPostId === post.id || post.state === 'ignored'}
                    >
                      {availableTemplates.map((template) => (
                        <option key={template.templateKey} value={template.templateKey}>{template.label}</option>
                      ))}
                    </select>
                    <small>{availableTemplates.find((template) => template.templateKey === post.template_key)?.description || availableTemplates[0].description}</small>
                  </label>
                )}
                <p className="social-post__caption">{post.caption || 'No caption provided.'}</p>
                {post.failure_detail && <p className="social-post__reason">{post.failure_detail}</p>}
                <time>{formatSocialPostDate(post.published_at)}</time>
                <div className="social-post__actions">
                  {post.state === 'ignored' ? (
                    <button className="social-post__action--primary" type="button" onClick={() => restorePost(post.id)} disabled={busyPostId === post.id}>Return to review</button>
                  ) : (
                    <button className="social-post__action--quiet" type="button" onClick={() => ignorePost(post.id)} disabled={busyPostId === post.id}>Ignore</button>
                  )}
                  {presentationReady ? (
                    <>
                      <button className="social-post__action--primary" type="button" onClick={() => createPresentation(post.id)} disabled={busyPostId === post.id}>{busyPostId === post.id ? 'Working…' : post.overlay_id ? 'Preview presentation' : 'Create presentation'}</button>
                      <button className="social-post__action--campaign" type="button" onClick={() => createCampaign(post)} disabled={busyPostId === post.id}>
                        {post.campaign_id ? 'Open campaign' : 'Create Campaign'}
                      </button>
                      <button type="button" onClick={() => playPresentation(post.id)} disabled={busyPostId === post.id || !selectedDevice?.id} title={!selectedDevice?.id ? 'Select a TV first' : `Play on ${selectedDevice.name || 'selected TV'}`}>Play on selected TV</button>
                    </>
                  ) : (
                    <button className="social-post__action--primary" type="button" onClick={() => importPost(post.id)} disabled={busyPostId === post.id || !importablePost || post.state !== 'discovered'}>{busyPostId === post.id ? 'Importing…' : 'Keep & import'}</button>
                  )}
                </div>
                <div className="social-post__links">
                  {post.permalink && <a href={post.permalink} target="_blank" rel="noreferrer">View on Facebook</a>}
                  {post.permalink && <button type="button" onClick={() => openTrackedCta(post.id)} disabled={busyPostId === post.id}>Test tracked link</button>}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {pageCount > 1 && <div className="social-content__pagination"><button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>Previous</button><span>Page {page} of {pageCount}</span><button type="button" disabled={page >= pageCount || loading} onClick={() => setPage((value) => value + 1)}>Next</button></div>}
    </section>
  );
}
